import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faStop, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import DropdownButton from 'components/buttons/DropdownButton';
import { DropdownButtonItem } from 'types/ButtonTypes';
import { useElapsedTime, useTimer } from 'services/timer/TimerContext';
import TimeRowForm from '../timesheets/TimeRowForm';
import { useModal } from 'components/modals/ModalContext';
import TicketReporterEmailModal from './TicketReporterEmailModal';
import { Task } from '../../views/tasks/TaskTypes';
import { renderTicketStatusBadge } from './TicketStatusBadge';
import TicketComposer from './TicketComposer';
import TicketConversation from './TicketConversation';
import { Ticket, TicketMessage } from './TicketTypes';
import '../../style/scss/tickets.scss';

interface TicketChatProps {
    ticket: Ticket;
    messages: TicketMessage[];
    tasks: Task[];
    onRefresh: () => void;
    onSwitchReporter?: () => void; // Shows the switch requester option in the more menu when given
    onDelete?: () => void; // Shows the delete option in the more menu when given
    onOpenInServicedesk?: () => void; // Shows the open in service desk option in the more menu when given
    onCreateProposal?: () => void; // Shows the create proposal action when given
    onCreateJob?: () => void; // Shows the create job action when given
    onCreateDeal?: () => void; // Shows the create deal action when given
    onStartTimer?: () => void; // Shows the start timer action when given
}

// The chat card of a ticket with its header, the conversation and the composer
const TicketChat: React.FC<TicketChatProps> = ({ ticket, messages, tasks, onRefresh, onSwitchReporter, onDelete, onOpenInServicedesk, onCreateProposal, onCreateJob, onCreateDeal, onStartTimer }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const moreMenuRef = useRef<HTMLDivElement>(null);
    const { runningTimer, refreshTimer } = useTimer();

    // The timer of the current user which runs on this ticket
    const ticketTimer = runningTimer && runningTimer.ticket?.id === ticket.id ? runningTimer : null;
    const elapsedTime = useElapsedTime(ticketTimer?.start_datetime);

    // The actions which create a proposal, job or deal from the ticket or start a timer on it
    const actionItems: DropdownButtonItem[] = [
        ...(onCreateProposal ? [{ label: 'ticket.actions.create_proposal_label', onClick: onCreateProposal }] : []),
        ...(onCreateJob ? [{ label: 'ticket.actions.create_job_label', onClick: onCreateJob }] : []),
        ...(onCreateDeal ? [{ label: 'ticket.actions.create_deal_label', onClick: onCreateDeal }] : []),
        ...(onStartTimer && !ticketTimer ? [{ label: 'ticket.actions.start_timer_label', onClick: onStartTimer }] : []),
    ];

    // Open the stop form of the timer which runs on this ticket
    const handleStopTimer = () => {
        if (!ticketTimer) return;
        initializeModal(<TimeRowForm stopTimer={true} />, {
            title: t('timerow.timer.stop_title'),
            itemId: ticketTimer.id,
            modalSize: 'add-form-smaller',
            onSuccess: () => { refreshTimer(); onRefresh(); }
        });
    };

    // Close the more menu when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
                setMoreMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Open the modal to enter the email address of the requester
    const handleAddReporterEmail = () => {
        initializeModal(
            <TicketReporterEmailModal ticket={ticket} onSaved={onRefresh} />,
            { title: t('ticket.general.add_reporter_email_title'), modalSize: 'extra-small' }
        );
    };

    return (
        <div className='ticket-chat'>
            <div className='ticket-chat-header'>
                <div className='ticket-chat-title'>
                    <h6>
                        {ticket.reporter_display_name || t('ticket.general.ticket_title', { number: ticket.number })}
                        {ticket.reporter_email && !ticket.reporter_email_on_own_domain && (
                            <span className='ticket-chat-email'>{ticket.reporter_email}</span>
                        )}
                        {ticket.reporter_email && ticket.reporter_email_on_own_domain && (
                            <span className='ticket-chat-email ticket-chat-email-missing tooltip-icon' onClick={handleAddReporterEmail}>
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {ticket.reporter_email}
                                <span className='tooltip'>{t('ticket.general.reporter_email_own_domain_tooltip')}</span>
                            </span>
                        )}
                        {!ticket.reporter_email && ticket.reporter && (
                            <span className='ticket-chat-email ticket-chat-email-missing' onClick={handleAddReporterEmail}>
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {t('ticket.general.no_reporter_email_label')}
                            </span>
                        )}
                    </h6>
                    <span className='ticket-chat-subject'>{ticket.subject}</span>
                </div>
                <div className='ticket-chat-header-right'>
                    {ticketTimer && (
                        <button type='button' className='button button-small button-primary timer-button timer-running ticket-timer' onClick={handleStopTimer}>
                            <FontAwesomeIcon icon={faStop} className='timer-icon' />
                            <span className='timer-label'>{elapsedTime}</span>
                        </button>
                    )}
                    {actionItems.length > 0 && (
                        <DropdownButton
                            items={actionItems}
                            label='ticket.actions.actions_label'
                            buttonType='primary'
                            size='extra-small'
                            onlyViewRestriction={true}
                            customClass='ticket-chat-actions-button'
                        />
                    )}
                    {renderTicketStatusBadge(ticket.status, ticket.new_reply, true)}
                    {(onOpenInServicedesk || onSwitchReporter || onDelete) && (
                        <div className='dropdown-button ticket-chat-more-menu' ref={moreMenuRef}>
                            <button type='button'
                                    className={`button button-extra-small button-secondary ticket-chat-more-button ${moreMenuOpen ? 'button-active' : ''}`}
                                    onClick={() => setMoreMenuOpen(!moreMenuOpen)}>
                                <FontAwesomeIcon icon={faEllipsis} />
                            </button>
                            {moreMenuOpen && (
                                <div className='dropdown-button-menu'>
                                    {onOpenInServicedesk && (
                                        <div className='dropdown-button-item' onClick={() => { setMoreMenuOpen(false); onOpenInServicedesk(); }}>
                                            {t('ticket.general.open_in_servicedesk_label')}
                                        </div>
                                    )}
                                    {onSwitchReporter && (
                                        <div className='dropdown-button-item' onClick={() => { setMoreMenuOpen(false); onSwitchReporter(); }}>
                                            {t('ticket.general.switch_requester_label')}
                                        </div>
                                    )}
                                    {onDelete && (
                                        <div className='dropdown-button-item' onClick={() => { setMoreMenuOpen(false); onDelete(); }}>
                                            {t('ticket.general.delete_request_label')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <TicketConversation ticket={ticket} messages={messages} tasks={tasks} onTaskChanged={onRefresh} />
            <TicketComposer key={ticket.id} ticket={ticket} onSaved={onRefresh} />
        </div>
    );
};

export default TicketChat;
